/**
 * A FILA DO APARELHO — o que o tratador gravou e o servidor ainda não sabe.
 *
 * Até aqui o app mandava cada registro direto para o servidor no instante do
 * clique. Na beira do tanque, sem sinal, isso significava uma coisa só: o
 * trato não existiu. O `CLAUDE.md` deste projeto sempre mandou o contrário —
 * «dados gravam primeiro no aparelho e sincronizam depois, sempre, sem
 * exceção» — e o código é que estava fora da própria regra.
 *
 * AGORA TODO REGISTRO NASCE AQUI. Trato, leitura e falta são escritos no
 * aparelho primeiro, com identificador próprio, e só sobem quando o tratador
 * aperta Sincronizar.
 *
 * POR QUE O ENVIO NÃO É AUTOMÁTICO — decisão do gestor, e ela tem razão de
 * ser. Envio automático esconde do tratador que existem duas memórias: ele
 * nunca aprende que o app guarda antes de mandar, e no dia em que a fila não
 * esvaziar não vai entender o que está vendo. O botão transforma a fila em
 * coisa visível e em gesto de quem trata.
 *
 * O IDENTIFICADOR NASCE AQUI, e é ele que torna reenviar seguro. Se a gravação
 * der certo no banco e a resposta se perder, o aparelho manda de novo; o
 * servidor esbarra na chave primária e não faz nada. Reenvio duplicado é
 * impossível por construção, não por cuidado.
 *
 * ARMAZENAMENTO: `localStorage`. São poucos registros por dia, e ele sobrevive
 * a fechar o app e a desligar o celular. Não sobrevive a limpar os dados do
 * navegador — por isso o contador existe, e por isso ele mostra o número em
 * destaque em vez de esconder a fila.
 */

import { ESPERA_MAXIMA_MS, pedir } from './rede'

const CHAVE = 'cunhatai.fila'

export type TipoDeRegistro = 'leitura' | 'trato' | 'falta' | 'biometria' | 'despesca' | 'movimentacao' | 'recepcao' | 'aplicacao' | 'perda' | 'anular_trato' | 'mov_racao' | 'fechar_pilha' | 'anular_fechamento' | 'anular_mov_racao' | 'remessa' | 'encerrar_ciclo' | 'anular_remessa' | 'nota_racao'

export interface Pendente {
  /** O identificador que vai para o banco. Na falta geral, o primeiro deles. */
  id: string
  tipo: TipoDeRegistro
  /** O corpo exato que será enviado, já com o `id` dentro. */
  corpo: Record<string, unknown>
  /** O dia a que o registro se refere — não o dia em que foi digitado. */
  dia: string
  /** Para a tela somar o que ainda não subiu ao que o servidor já sabe. */
  ciclo_id: string | null
  tanque_id: string | null
  /** «T-4», para a fila poder se mostrar sem consultar o servidor. */
  rotulo: string | null
  /** «2 sacos», «3,8 mg/L» — o registro em uma linha, para conferência. */
  resumo: string | null
  criado_em: string
  /**
   * Recusa do servidor. Erro de rede NÃO entra aqui: esse é para tentar de
   * novo. Aqui só entra o que nunca vai dar certo sozinho — data inválida,
   * ciclo que não existe —, e que precisa de alguém olhando.
   */
  recusa?: string
  /**
   * Da última vez, a conexão falhou neste registro — o motivo, dito. Não é
   * recusa: o registro pode estar certo e o sinal ruim. Some quando ele sobe.
   */
  falha?: string
  /** Quantas vezes seguidas a conexão falhou NESTE registro. */
  falhas_seguidas?: number
}

const ROTA: Record<TipoDeRegistro, string> = {
  leitura: 'ambiente',
  trato: 'tratos',
  falta: 'faltas',
  biometria: 'biometrias',
  despesca: 'despescas',
  movimentacao: 'movimentacoes',
  recepcao: 'recepcoes',
  aplicacao: 'aplicacoes',
  perda: 'perdas',
  anular_trato: 'anular-trato',
  mov_racao: 'mov-racao',
  fechar_pilha: 'fechar-pilha',
  anular_fechamento: 'anular-fechamento',
  anular_mov_racao: 'anular-mov-racao',
  remessa: 'remessas-de-peixe',
  encerrar_ciclo: 'encerrar-ciclo',
  anular_remessa: 'anular-remessa',
  nota_racao: 'nota-racao',
}

export function novoId(): string {
  const c: Crypto | undefined = typeof crypto === 'undefined' ? undefined : crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  // Navegador antigo: sorteio com os mesmos 122 bits, formato idêntico.
  const b = new Uint8Array(16)
  if (!c) throw new Error('este navegador não sorteia números seguros')
  c.getRandomValues(b)
  b[6] = ((b[6] ?? 0) & 0x0f) | 0x40
  b[8] = ((b[8] ?? 0) & 0x3f) | 0x80
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('')
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

function ler(): Pendente[] {
  try {
    const bruto = localStorage.getItem(CHAVE)
    const lista = bruto ? (JSON.parse(bruto) as unknown) : []
    return Array.isArray(lista) ? (lista as Pendente[]) : []
  } catch {
    return []
  }
}

function escrever(lista: Pendente[]): void {
  try {
    localStorage.setItem(CHAVE, JSON.stringify(lista))
  } catch {
    // Armazenamento cheio ou bloqueado. Não há o que fazer aqui além de não
    // derrubar a tela; o contador vai denunciar sozinho que nada entrou.
  }
  avisar()
}

// ---------------------------------------------------------------------------
// Quem está ouvindo
// ---------------------------------------------------------------------------

const ouvintes = new Set<() => void>()

function avisar(): void {
  for (const f of ouvintes) f()
}

/** Avisa a cada mudança da fila. Devolve a função que cancela o aviso. */
export function assinar(f: () => void): () => void {
  ouvintes.add(f)
  return () => {
    ouvintes.delete(f)
  }
}

// ---------------------------------------------------------------------------
// Entrar na fila
// ---------------------------------------------------------------------------

export function enfileirar(
  tipo: TipoDeRegistro,
  corpo: Record<string, unknown>,
  onde: {
    dia: string
    ciclo_id?: string | null
    tanque_id?: string | null
    rotulo?: string | null
    resumo?: string | null
  },
): Pendente {
  const id = String(corpo['id'] ?? novoId())
  const item: Pendente = {
    id,
    tipo,
    corpo: { ...corpo, id },
    dia: onde.dia,
    ciclo_id: onde.ciclo_id ?? null,
    tanque_id: onde.tanque_id ?? null,
    rotulo: onde.rotulo ?? null,
    resumo: onde.resumo ?? null,
    criado_em: new Date().toISOString(),
  }
  escrever([...ler(), item])
  return item
}

export function pendentes(): Pendente[] {
  return ler()
}

export function quantosPendentes(): number {
  return ler().length
}

/**
 * TIRA UM REGISTRO DA FILA.
 *
 * Enquanto está aqui, o registro nunca existiu para o banco — apagá-lo é
 * apagar de verdade, sem deixar rastro e sem precisar de lançamento que anule.
 * É a única janela em que desfazer é grátis, e ela fecha no Sincronizar.
 */
export function descartar(id: string): void {
  escrever(ler().filter((p) => p.id !== id))
}

/** Esvazia a fila inteira. Só para quando alguém errou em série. */
export function descartarTudo(): void {
  escrever([])
}

// ---------------------------------------------------------------------------
// O que a tela precisa somar
//
// Enquanto um registro está na fila, o servidor não sabe dele — e a tela não
// pode fingir que nada aconteceu. Sem isto, o tratador registraria o trato,
// veria o tanque continuar dizendo «nada oferecido hoje», e trataria de novo.
// ---------------------------------------------------------------------------

export function tratosPendentesDe(cicloId: string, dia: string): Pendente[] {
  return ler().filter((p) => p.tipo === 'trato' && p.ciclo_id === cicloId && p.dia === dia)
}

/**
 * Os tratos com ANULAÇÃO esperando na fila.
 *
 * Existe pela mesma razão que `tratosPendentesDe`: enquanto a fila não sobe, o
 * aparelho sabe de uma coisa que o servidor não sabe, e a tela tem de mostrar
 * o que o aparelho sabe. Sem isto, anular não mudava nada na tela — o trato
 * continuava com o ✓ e somando no total —, e ação que não dá sinal de ter
 * acontecido é ação que se repete. Foi assim que o T-8 recebeu três tratos.
 *
 * Devolve conjunto e não lista porque a pergunta de quem chama é sempre «este
 * trato aqui está anulado?», uma vez por linha desenhada.
 */
export function anulacoesPendentes(): Set<string> {
  const ids = new Set<string>()
  for (const p of ler()) {
    if (p.tipo !== 'anular_trato') continue
    const alvo = p.corpo['trato_id']
    if (typeof alvo === 'string') ids.add(alvo)
  }
  return ids
}

export function faltasPendentesDe(cicloId: string, dia: string): number {
  return ler().filter((p) => p.tipo === 'falta' && p.ciclo_id === cicloId && p.dia === dia)
    .length
}

export function leiturasPendentesDe(tanqueId: string, dia: string): Pendente[] {
  return ler().filter((p) => p.tipo === 'leitura' && p.tanque_id === tanqueId && p.dia === dia)
}

// ---------------------------------------------------------------------------
// Subir
// ---------------------------------------------------------------------------

export interface ResultadoDaSincronia {
  enviados: number
  restantes: number
  /** Não há conexão: o pedido nem saiu. O que sobrou continua na fila, intacto. */
  semSinal: boolean
  /**
   * A conexão falhou: o pedido saiu e a resposta não voltou direito — o tempo
   * acabou, ou a Cloudflare devolveu um erro passageiro (5xx, 408, 429). É
   * sinal ruim, e não recusa: a fila para, guarda tudo, e tenta de novo depois.
   */
  falhou: boolean
  /** O crachá deste aparelho caiu (401). É sessão, não é o registro. */
  semAcesso: boolean
  /**
   * Quantos o servidor recusou NESTA sincronização. Recusa antiga de um
   * registro que nem foi tentado desta vez não entra: o recado diria «o servidor
   * recusou» depois de nada ter chegado a ele.
   */
  recusados: number
  /**
   * O registro que parou a fila e já falhou `FALHAS_PARA_APONTAR` vezes
   * seguidas. Se o sinal está bom, é ele que está travando — e a tela diz qual.
   */
  travado: Pick<Pendente, 'tipo' | 'rotulo' | 'resumo'> & { vezes: number } | null
}

/** O registro sem a marca de falha de conexão. A recusa, se houver, fica. */
function semFalha(p: Pendente): Pendente {
  const limpo: Pendente = { ...p }
  delete limpo.falha
  delete limpo.falhas_seguidas
  return limpo
}

/** O registro sem marca nenhuma — nem recusa, nem falha. */
function semMarcas(p: Pendente): Pendente {
  const limpo = semFalha(p)
  delete limpo.recusa
  return limpo
}

/**
 * Quantas falhas seguidas do MESMO registro até a tela apontá-lo.
 *
 * Uma falha é sinal ruim, e passa. Três seguidas do mesmo registro, com o resto
 * da fila parado atrás dele, é o registro: há erro que se repete sempre no mesmo
 * pedido (o servidor estourando o limite de processamento com aquele conteúdo),
 * e sem apontar qual é, a única saída seria apagar a fila inteira.
 *
 * SÓ CONTA A FALHA EM QUE O SERVIDOR RESPONDEU. O tempo esgotado é quase sempre
 * o sinal: com uma barra, três Sincronizar de manhã dariam «é ele que trava», e
 * a pessoa apagaria um trato que estava certo — e o tanque seria tratado de
 * novo. E «não há conexão» zera a conta: «seguidas» é seguidas.
 */
const FALHAS_PARA_APONTAR = 3

/**
 * Manda a fila inteira, na ordem em que foi gravada.
 *
 * A ORDEM IMPORTA: o trato do dia tira o número de sacos do que já saiu, e a
 * sequência do trato no banco é contada a partir do que já está lá. Mandar
 * fora de ordem não perde dado, mas embaralha a sequência do dia.
 *
 * SEM SINAL PARA A FILA INTEIRA. Se o primeiro não foi, os outros também não
 * vão, e insistir só gasta bateria. Já uma RECUSA do servidor não para nada:
 * ela é daquele registro e dos outros não se sabe nada ainda.
 */
export async function sincronizar(
  esperaMaximaMs: number = ESPERA_MAXIMA_MS,
): Promise<ResultadoDaSincronia> {
  const lista = ler()
  let enviados = 0
  let semSinal = false
  let falhou = false
  let semAcesso = false
  let recusados = 0
  let travado: ResultadoDaSincronia['travado'] = null
  const sobraram: Pendente[] = []

  for (let i = 0; i < lista.length; i += 1) {
    const item = lista[i]
    if (!item) continue
    // QUALQUER UM DOS TRÊS PARA A FILA INTEIRA, e na ordem: o registro
    // seguinte pode depender deste (a nota depende da compra), e mandá-lo na
    // frente seria trocar um problema de sinal por uma recusa de verdade.
    if (semSinal || falhou || semAcesso) {
      sobraram.push(item)
      continue
    }
    // A CONVERSA MORA EM `rede.ts`, a mesma das telas: limite de espera,
    // erro passageiro e recusa classificados num lugar só.
    const d = await pedir(ROTA[item.tipo], item.corpo, esperaMaximaMs)

    if (d.tipo === 'ok') {
      // Inclusive `{ repetido: true }`: para o aparelho, «já estava lá» é
      // sucesso. É exatamente o caso do reenvio, e o registro sai da fila.
      enviados += 1
      continue
    }
    if (d.tipo === 'sem_conexao') {
      // O pedido nem saiu: não é culpa do registro, e a conta de falhas
      // seguidas recomeça — a de sexta não soma com a de sábado.
      semSinal = true
      sobraram.push(semFalha(item))
      continue
    }
    if (d.tipo === 'sem_acesso') {
      // O crachá caiu. Não é recusa do registro; é sessão. Guarda tudo.
      semAcesso = true
      sobraram.push(item)
      continue
    }
    if (d.tipo === 'falhou') {
      // Um item que já tinha RECUSA de uma tentativa anterior a mantém: é o
      // último veredito que o servidor deu sobre ele, e desta vez não houve
      // veredito nenhum.
      falhou = true
      if (!d.respondeu) {
        // Tempo esgotado: diz o motivo, mas não pesa contra o registro.
        sobraram.push({ ...item, falha: d.motivo })
        continue
      }
      const vezes = (item.falhas_seguidas ?? 0) + 1
      sobraram.push({ ...item, falha: d.motivo, falhas_seguidas: vezes })
      if (vezes >= FALHAS_PARA_APONTAR) {
        travado = { tipo: item.tipo, rotulo: item.rotulo, resumo: item.resumo, vezes }
      }
      continue
    }
    // RECUSA: o servidor respondeu e explicou. Fica marcada, e a fila segue.
    recusados += 1
    sobraram.push({ ...semMarcas(item), recusa: d.erro })
  }

  escrever(sobraram)
  return {
    enviados,
    restantes: sobraram.length,
    semSinal,
    falhou,
    semAcesso,
    recusados,
    travado,
  }
}
